import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { staggeredBaseQuery } from "./staggeredBaseQuery";
import { TrainingOrderMetadata, TrainingOrder, trainingOrderSearchParams, TrainingInsightsView, TrainingOrderChange, TrainingReport } from "../../common/Training";
import { PaginatedResponse } from "../../common/PaginatedResponse";

export const trainingOrderApi = createApi({
    reducerPath: "trainingOrderApi",
    tagTypes: ["TrainingOrder", "TrainingOrderHistory"],
    baseQuery: staggeredBaseQuery(""),
    endpoints: (builder) => ({
        deleteTrainingOrder: builder.mutation<boolean, String>({
            query: (trainingOrderId) => {
                return {
                    method: "DELETE",
                    url: `trainingOrder/${trainingOrderId}`,
                }
            }
        }),
        getTrainingOrderMetadata: builder.query<
            TrainingOrderMetadata,
            { fleetId: number | undefined, vesselId: number | undefined }
        >({
            query: ({ fleetId, vesselId }) => {
                return {
                    method: "GET",
                    url: `trainingOrder/metadata${
                        fleetId != null ? "?fleetId=" + fleetId : ""}${
                        vesselId != null ? "?vesselId=" + vesselId : ""}`
                };
            }
        }),
        getTrainingOrderInsight: builder.query<
            TrainingInsightsView,
            { fleetId: number | undefined, vesselId: number | undefined }>({
                query: ({ fleetId, vesselId }) => {
                    return {
                        method: "GET",
                        url: `trainingOrder/insights${
                            fleetId != null ? "?fleetId=" + fleetId : ""}${
                            vesselId != null ? "?vesselId=" + vesselId : ""}`
                    };
                }
            }),
        getTrainingOrdersByMariners: builder.query<TrainingOrder[], number[]>({
            query: (marinerIds) => {
                return {
                    method: "GET",
                    url: `trainingOrder/mariners/`,
                    body: marinerIds
                };
            }
        }),
        getTrainingOrdersByVessel: builder.query<TrainingOrder[], string>({
            query: (vesselId) => {
                return {
                    method: "GET",
                    url: `trainingOrder/vessel/${vesselId}`
                };
            }
        }),
        getTrainingOrders: builder.query<
            PaginatedResponse<TrainingOrder>,
            Partial<trainingOrderSearchParams>
        >({
            query: ({
                assigneeId,
                rating,
                compatibleWithRating,
                trainingOrderId,
                scheduled,
                completed,
                deleted,
                requestedByCaptain,
                modifiedByCaptain,
                profileId,
                page,
                size,
                sortColumn,
                sortDirection,
            }) => {
                let url = `trainingOrder/search${!!assigneeId ? "&assigneeId=" + assigneeId : ""
                    }${!!rating ? "&rating=" + rating : ""}${!!compatibleWithRating
                        ? "&compatibleWithRating=" + compatibleWithRating
                        : ""
                    }${!!trainingOrderId ? "&trainingOrderId=" + trainingOrderId : ""}${!!completed || completed === false ? "&completed=" + completed : ""
                    }${!!scheduled || scheduled === false ? "&scheduled=" + scheduled : ""}${!!deleted || deleted === false ? "&deleted=" + deleted : ""}${!!requestedByCaptain || requestedByCaptain === false ? "&requestedByCaptain=" + requestedByCaptain : ""}${!!modifiedByCaptain || modifiedByCaptain === false ? "&modifiedByCaptain=" + modifiedByCaptain : ""}${!!profileId ? "&profileId=" + profileId : ""}${"&page=" + page}${"&size=" + size}${!!sortColumn && sortColumn !== "" ? "&sortColumn=" + sortColumn : ""
                    }${!!sortColumn && sortColumn !== ""
                        ? "&sortDirection=" + sortDirection?.toUpperCase()
                        : ""
                    }
                    `;
                url = url.replace("trainingOrder/search&", "trainingOrder/search?");
                return {
                    method: "GET",
                    url,
                };
            },
        }),
        getTrainingOrder: builder.query<TrainingOrder, String>({
            query: (trainingOrderId) => {
                return {
                    method: "GET",
                    url: `trainingOrder/${trainingOrderId}`,
                };
            }
        }),
        getTrainingOrderHistory: builder.query<TrainingOrderChange[], string>({
            query: (trainingOrderId) => {
              return {
                method: "GET",
                url: `trainingOrder/${trainingOrderId}/history`,
              };
            },
            providesTags: (result) =>
              result
                ? [
                    ...result.map(({ id }) => ({
                      type: "TrainingOrderHistory" as const,
                      id,
                    })),
                    { type: "TrainingOrderHistory", id: "LIST" },
                  ]
                : [{ type: "TrainingOrderHistory", id: "LIST" }],
          }),
        createTrainingOrder: builder.mutation<TrainingOrder, Partial<TrainingOrder>>({
            query: (trainingOrder) => {
                return {
                    method: "POST",
                    url: "trainingOrder/",
                    body: trainingOrder,
                };
            }
            // may need to invalidate tags
        }),
        updateTrainingOrder: builder.mutation<TrainingOrder,
            { trainingOrderId: string; trainingOrder: Partial<TrainingOrder>; }
        >({
            query: ({ trainingOrderId, trainingOrder, }) => {
                return {
                    method: "PATCH",
                    url: `trainingOrder/${trainingOrderId}`,
                    body: trainingOrder,
                };
            }
            // may need to invalidate tags

        }),
        markTrainingOrderDeleted: builder.mutation<
            TrainingOrder,
            { trainingOrderId: string; note: string }
        >({
            query: ({ trainingOrderId, note }) => {
                return {
                    method: "POST",
                    url: `trainingOrder/${trainingOrderId}/delete`,
                    body: { note },
                }
            }
        }),
        getTrainingOrdersByMariner: builder.query<TrainingOrder[], number>({
            query: (marinerId) => {
                return {
                    method: "GET",
                    url: `trainingOrder/mariner/${marinerId}`
                };
            }
        }),
        saveTrainingOrders: builder.mutation<
            { successCount: number; failedCount: number },
            { trainingOrders: Partial<TrainingOrder>[]; }
        >({
            async queryFn(
                { trainingOrders },
                _queryApi,
                _extraOptions,
                baseQuery
            ) {
                let successCount: number = 0;
                let failedCount: number = 0;
                let additionalError: string | undefined = undefined;
                for (const trainingOrder of trainingOrders) {
                    if (!trainingOrder.id) {
                        const response = await baseQuery({
                            url: `trainingOrder/`,
                            method: "POST",
                            body: trainingOrder
                        });
                        if ("error" in response) {
                            failedCount += 1;
                        } else {
                            successCount += 1;
                        }
                    } else {
                        const response = await baseQuery({
                            url: `trainingOrder/${trainingOrder.id}`,
                            method: "PATCH",
                            body: trainingOrder,
                        });
                        if ("error" in response) {
                            failedCount += 1;
                            // concurrent editing error
                            if (
                                (response.error?.data as any[])?.[0].errorCode === "trainingorder.update.edited.failed"
                            ) {
                                if (!additionalError) {
                                    additionalError = `Training Order has already been updated. Please reload the page and re-apply your changes. Affected Training Orders: ${trainingOrder.id}`;
                                } else {
                                    additionalError = additionalError + `, ${trainingOrder.id}`;
                                }
                            }
                        } else {
                            successCount += 1;
                        }
                    }
                }
                return { data: { successCount, failedCount, additionalError } };
            }
        }),
        getTrainingReport: builder.query<
          TrainingReport, 
          { startDate: string; endDate: string; fleetId: number | undefined; vesselId: number | undefined }
        >({
          query: ({ startDate, endDate, fleetId, vesselId }) => {
            let url = `trainingOrder/report?startDate=${startDate}&endDate=${endDate}`;
            if (fleetId) url += `&fleetId=${fleetId}`;
            if (vesselId) url += `&vesselId=${vesselId}`;
            return {
              method: "GET",
              url,
            };
          },
        }),
    })
});

export const {
    useDeleteTrainingOrderMutation,
    useGetTrainingOrderMetadataQuery,
    useGetTrainingOrderInsightQuery,
    useGetTrainingOrdersByMarinersQuery,
    useGetTrainingOrdersByVesselQuery,
    useGetTrainingOrdersQuery,
    useGetTrainingOrderQuery,
    useLazyGetTrainingOrderHistoryQuery,
    useCreateTrainingOrderMutation,
    useUpdateTrainingOrderMutation,
    useMarkTrainingOrderDeletedMutation,
    useGetTrainingOrdersByMarinerQuery,
    useSaveTrainingOrdersMutation,
    useGetTrainingReportQuery,
} = trainingOrderApi;
